import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NotificationSystem from 'react-notification-system';
import styled from 'styled-components';
import GlobalStyle from '../../../GlobalStyle';
import MultiColumnHeader from './MultiColumnHeader';
import Footer from '../../../folders/subtitles/Footer';
import Loading from '../../../folders/subtitles/Loading';
import Player from '../../../folders/subtitles/Player';
import ProgressBar from '../../../folders/subtitles/ProgressBar';
import MultiColumnSubtitles from './MultiColumnSubtitles';
import MultiColumnTimeline from './MultiColumnTimeline';
import Sub from '../../../libs/Sub';
import { getSubNodesfromVtt, file2vtt } from '../../../libs/readSub/SubUtils';
import { getKeyCode, getExt } from '../../../utils';
import { fetchVTTFile } from '../ReadSubtitleString';
import { debounce } from 'lodash';
import historyService from '../../../services/HistoryService';
import { IconButton, Tooltip } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import axios from 'axios';
import Swal from 'sweetalert2';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';

const Style = styled.div`
    display: flex;
    flex-direction: column;
    height: 100vh;
    width: 100%;
    overflow: hidden;
    
    .toolbar-container {
        display: flex;
        align-items: center;
        padding: 5px;
        background-color: rgba(30, 30, 30, 0.95);
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        overflow-x: auto;
        
        @media (min-width: 768px) {
            padding: 5px 10px;
        }
    }
    
    .main-container {
        display: flex;
        flex: 1;
        flex-direction: column;
        height: calc(100vh - 45px); /* Adjust for toolbar height */
        overflow: hidden;
    }

    .content-container {
        display: flex;
        flex-direction: column;
        flex: 1;
        overflow: hidden;
        
        @media (min-width: 768px) {
            flex-direction: row;
        }
    }
    
    .player-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        width: 100%;
        min-height: 200px;
        padding: 10px;
        
        @media (min-width: 768px) {
            flex: 1;
            min-height: auto;
            /* Dynamically adjust based on timeline visibility */
            transition: max-height 0.3s ease-in-out;
        }
    }

    .subtitles-container {
        flex: 1;
        width: 100%;
        overflow: auto;
        
        @media (min-width: 768px) {
            /* Dynamically adjust based on timeline visibility */
            transition: max-height 0.3s ease-in-out;
        }
    }
    
    .timeline-container {
        width: 100%;
        overflow: hidden;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        background-color: #1e1e1e;
        transition: height 0.3s ease-in-out;
    }
    
    .timeline-collapse-control {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 24px;
        background-color: #2c2c2c;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        cursor: pointer;
        user-select: none;
        color: rgba(255, 255, 255, 0.7);
        font-size: 12px;
        
        &:hover {
            background-color: #3c3c3c;
            color: rgba(255, 255, 255, 0.9);
        }
        
        .collapse-icon {
            margin-left: 8px;
            font-size: 18px;
            line-height: 1;
        }
        
        /* Make it more prominent when timeline is collapsed */
        &.collapsed {
            background-color: #4c4c4c;
            color: rgba(255, 255, 255, 0.9);
            font-weight: bold;
        }
    }
    
    .toolbar-group {
        display: flex;
        align-items: center;
        margin-right: 8px;
        padding-right: 8px;
        border-right: 1px solid rgba(255, 255, 255, 0.1);
        white-space: nowrap;
        
        &:last-child {
            border-right: none;
        }
        
        @media (min-width: 768px) {
            margin-right: 12px;
            padding-right: 12px;
        }
    }
    
    .toolbar-spacer {
        flex: 1;
    }
    
    .column-selector {
        display: flex;
        flex-direction: column;
        padding: 10px;
        background-color: rgba(40, 40, 40, 0.95);
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        
        @media (min-width: 768px) {
            flex-direction: row;
            align-items: center;
        }
    }
    
    .column-item {
        display: flex;
        flex-direction: column;
        margin-bottom: 10px;
        flex: 1;
        padding: 0 5px;
        
        @media (min-width: 768px) {
            margin-bottom: 0;
            margin-right: 10px;
        }
    }
    
    .column-item-header {
        font-weight: bold;
        margin-bottom: 5px;
        color: #fff;
    }
    
    /* New styles for header toggle button */
    .header-collapse-control {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 24px;
        background-color: #2c2c2c;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        cursor: pointer;
        user-select: none;
        color: rgba(255, 255, 255, 0.7);
        font-size: 12px;
        
        &:hover {
            background-color: #3c3c3c;
            color: rgba(255, 255, 255, 0.9);
        }
    }
`;

export default function MultiColumnSubtitleViewer() {
    const notificationSystem = useRef(null);
    const [player, setPlayer] = useState(null);
    const [loading, setLoading] = useState('');
    const [processing, setProcessing] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [playing, setPlaying] = useState(false);
    const [waveform, setWaveform] = useState(null);
    
    // Columns configuration - up to 3 columns
    const [columns, setColumns] = useState([
        { language: 'English', version: 'latest', subtitles: [], versions: [] },
        { language: null, version: 'latest', subtitles: [], versions: [] },
        { language: null, version: 'latest', subtitles: [], versions: [] }
    ]);
    
    // Window size state for responsive design
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });
    
    // Add a new state to track timeline visibility
    const [timelineExpanded, setTimelineExpanded] = useState(true);
    
    // Add this state to track header visibility
    const [headerExpanded, setHeaderExpanded] = useState(true);
    
    // Calculate timeline height based on expanded state
    const timelineHeight = timelineExpanded ? 460 : 24; // 24px for just the collapse control
    
    // Calculate available height for player and subtitles containers
    const contentMaxHeight = `calc(100vh - ${timelineHeight + 45}px)`; // 45px for toolbar

    const notify = useCallback(
        (obj) => {
            const notification = notificationSystem.current;
            notification.clearNotifications();
            notification.addNotification({
                position: 'tc',
                dismissible: 'none',
                autoDismiss: 2,
                message: obj.message,
                level: obj.level,
            });
        },
        [notificationSystem],
    );

    // Add this useEffect to handle window resizing
    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        }, 200);
        
        window.addEventListener('resize', handleResize);
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    
    const newSub = useCallback((item) => new Sub(item), []);

    const formatSub = useCallback(
        (sub) => {
            if (Array.isArray(sub)) {
                return sub.map((item) => newSub(item));
            }
            return newSub(sub);
        },
        [newSub],
    );
    
    // Handle file import for a column
    const handleImportFile = useCallback(async (columnIndex, file, language) => {
        if (!file || !language) {
            notify({
                message: 'Invalid file or language selection',
                level: 'error'
            });
            return;
        }
        
        const ext = getExt(file.name);
        
        // Only accept supported formats (vtt, srt, ass, json)
        if (!['vtt', 'srt', 'ass', 'json'].includes(ext)) {
            notify({
                message: `Unsupported file format: ${ext}. Please use VTT, SRT, ASS, or JSON files.`,
                level: 'error'
            });
            
            // Add Swal to show the error that this extension is not supported
            Swal.fire({
                title: 'Error!',
                text: 'The selected extension of file is not supported. Supported file formats (.srt, .vtt, .ass, .json)',
                icon: 'error',
                confirmButtonText: 'Close',
            });
            return;
        }
        
        setLoading(`Importing ${file.name} for ${language}...`);
        
        try {
            // Use file2vtt directly on the file object
            file2vtt(file)
                .then((vttContent) => {
                    // Parse the VTT content to subtitle nodes
                    const stateUpdated = getSubNodesfromVtt(vttContent);
                    
                    // Format parsed subtitles
                    const formattedSubtitles = stateUpdated.map(item => newSub(item));
                    
                    // Update the column with imported subtitles
                    setColumns(prev => {
                        const newColumns = [...prev];
                        newColumns[columnIndex] = { 
                            ...newColumns[columnIndex], 
                            subtitles: formattedSubtitles,
                            importedFile: file.name,
                            version: 'imported' // Mark as imported version
                        };
                        return newColumns;
                    });
                    
                    // Show success notification
                    notify({
                        message: `Successfully imported ${file.name} for ${language}`,
                        level: 'success'
                    });
                })
                .catch((err) => {
                    console.error(`Error processing file ${file.name}:`, err);
                    notify({
                        message: `Error processing file: ${err.message}`,
                        level: 'error'
                    });
                });
        } catch (error) {
            console.error(`Error importing file ${file.name}:`, error);
            notify({
                message: `Error importing file: ${error.message}`,
                level: 'error'
            });
        } finally {
            setLoading('');
        }
    }, [newSub, notify]);
    
    // Helper function to read file content
    const readFileContent = (file, fileExt) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            
            reader.onload = (event) => {
                try {
                    if (fileExt === 'json') {
                        // Parse JSON file
                        const jsonData = JSON.parse(event.target.result);
                        resolve(jsonData);
                    } else {
                        // Return text content for VTT and SRT
                        resolve(event.target.result);
                    }
                } catch (error) {
                    reject(new Error(`Failed to parse file: ${error.message}`));
                }
            };
            
            reader.onerror = () => {
                reject(new Error('Failed to read file'));
            };
            
            if (fileExt === 'json') {
                reader.readAsText(file);
            } else {
                reader.readAsText(file);
            }
        });
    };
    

    
    // Handle column language change
    const handleLanguageChange = useCallback(async (index, language) => {
        if (!language) {
            // If language is cleared, reset column
            setColumns(prev => {
                const newColumns = [...prev];
                newColumns[index] = { 
                    ...newColumns[index], 
                    language: null, 
                    version: 'latest', 
                    subtitles: [], 
                    versions: [] 
                };
                return newColumns;
            });
            return;
        }
        
        setLoading(`Loading ${language} subtitles...`);
        
        try {
            const videoProps = JSON.parse(localStorage.getItem('videoProps'));
            
            // Find language index in video props
            const langIndex = videoProps.videoLanguages.findIndex(
                lang => lang.languageName === language
            );
            
            if (langIndex === -1) {
                throw new Error(`Language ${language} not found`);
            }
            
            // Fetch versions for this language
            const versionsResponse = await historyService.getVideoHistory(videoProps.id, language);
            console.log('Versions fetched:', versionsResponse);

            const versions = versionsResponse ||[];
            // Fetch latest subtitle data
            const vttData = await fetchVTTFile(videoProps.videoLanguages[langIndex].vttFileUrl);
            
            if (vttData) {
                const stateUpdated = getSubNodesfromVtt(vttData);
                const formattedSubtitles = stateUpdated.map(item => newSub(item));
                
                setColumns(prev => {
                    const newColumns = [...prev];
                    newColumns[index] = { 
                        ...newColumns[index], 
                        language, 
                        version: 'latest', 
                        subtitles: formattedSubtitles,
                        versions: versions.versions 
                    };
                    return newColumns;
                });
            }
        } catch (error) {
            console.error(`Error loading language ${language}:`, error);
            notify({
                message: `Error loading ${language} subtitles: ${error.message}`,
                level: 'error'
            });
        } finally {
            setLoading('');
        }
    }, [newSub]);
    
    
    // Handle version change for a column
    const handleVersionChange = useCallback(async (columnIndex, versionId) => {
        const column = columns[columnIndex];
        
        if (!column.language) {
            return;
        }
        
        // If the user selected "imported" version, we already have the data loaded
        if (versionId === "imported") {
            // Just update the version, no need to fetch new data
            setColumns(prev => {
                const newColumns = [...prev];
                newColumns[columnIndex] = { 
                    ...newColumns[columnIndex], 
                    version: 'imported'
                };
                return newColumns;
            });
            return;
        }
        
        setLoading(`Loading version ${versionId} for ${column.language}...`);
        
        try {
            const videoProps = JSON.parse(localStorage.getItem('videoProps'));
            const authToken = JSON.parse(localStorage.getItem('authToken'));
            
            let vttUrl;
            
            if (versionId === 'latest') {
                // Use the latest version from video props
                const langIndex = videoProps.videoLanguages.findIndex(
                    lang => lang.languageName === column.language
                );
                
                if (langIndex === -1) {
                    throw new Error(`Language ${column.language} not found`);
                }
                
                vttUrl = videoProps.videoLanguages[langIndex].vttFileUrl;
            } else {
                // Find the version in the column's versions array
                const selectedVersion = column.versions.find(v => v.id === versionId);
                
                if (selectedVersion && selectedVersion.fileUrl) {
                    // Use the fileUrl directly if available
                    vttUrl = selectedVersion.fileUrl;
                } else {
                    // Otherwise fetch it from the API
                    const response = await fetch(
                        `/api/v1/subtitles/version/${videoProps.id}/${column.language}/${versionId}`,
                        {
                            headers: {
                                'Authorization': `Bearer ${authToken}`
                            }
                        }
                    );
                    
                    if (!response.ok) {
                        throw new Error(`Error fetching version: ${response.statusText}`);
                    }
                    
                    const data = await response.json();
                    vttUrl = data.vttUrl;
                }
            }
            
            // Fetch and parse the VTT file
            const vttData = await fetchVTTFile(vttUrl);
            
            if (vttData) {
                const stateUpdated = getSubNodesfromVtt(vttData);
                const formattedSubtitles = stateUpdated.map(item => newSub(item));
                
                setColumns(prev => {
                    const newColumns = [...prev];
                    newColumns[columnIndex] = { 
                        ...newColumns[columnIndex], 
                        version: versionId,
                        subtitles: formattedSubtitles
                    };
                    return newColumns;
                });
            }
        } catch (error) {
            console.error(`Error loading version ${versionId}:`, error);
            notify({
                message: `Error loading version: ${error.message}`,
                level: 'error'
            });
        } finally {
            setLoading('');
        }
    }, [columns, newSub, notify]);
    
    
    
    const onKeyDown = useCallback(
        (event) => {
            const keyCode = getKeyCode(event);
            const KEY_TAB = 9;
            
            switch (keyCode) {
                case KEY_TAB:
                    event.preventDefault();
                    if (player) {
                        if (player.element.paused) {
                            player.element.play();
                        } else {
                            player.element.pause();
                        }
                    }
                    break;
                    
                default:
                    break;
            }
        },
        [player],
    );
    
    // Update current subtitle index based on playback time
    useEffect(() => {
        // Use the first active column with subtitles for current index tracking
        const activeColumn = columns.find(col => col.language && col.subtitles.length > 0);
        
        if (!activeColumn) return;
        
        const currentSubIndex = activeColumn.subtitles.findIndex(
            (item) => item.startTime <= currentTime && item.endTime > currentTime
        );
        
        if (currentSubIndex === -1) {
            // Find the last subtitle that appeared before the current time
            const lastSubtitleBeforeGap = activeColumn.subtitles.reduce((closest, item, index) => {
                if (item.endTime <= currentTime && 
                    (closest === -1 || item.endTime > activeColumn.subtitles[closest].endTime)) {
                    return index;
                }
                return closest;
            }, -1);
            
            if (lastSubtitleBeforeGap !== -1) {
                setCurrentIndex(lastSubtitleBeforeGap);
            }
        } else {
            setCurrentIndex(currentSubIndex);
        }
    }, [currentTime, columns]);
    
    // Initial setup
    useEffect(() => {
        // Initial load of English subtitles
        handleLanguageChange(0, 'English');
        
        // Set up event listeners
        window.addEventListener('keydown', onKeyDown);
        
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [onKeyDown, handleLanguageChange]);
    
    // Set up handleSetTime function for timeline interaction
    const handleSetTime = useCallback((timeInSeconds) => {
        if (player && player.element) {
            player.element.currentTime = timeInSeconds;
        }
    }, [player]);
    
    // Toggle timeline visibility
    const toggleTimeline = () => {
        setTimelineExpanded(!timelineExpanded);
    };
    
    // Add a toggle function for the header
    const toggleHeader = useCallback(() => {
        setHeaderExpanded(prev => !prev);
    }, []);
    
    // Create common props
    const props = {
        player,
        setPlayer,
        columns,
        setColumns,
        currentTime,
        setCurrentTime,
        currentIndex,
        setCurrentIndex,
        playing,
        setPlaying,
        waveform,
        setWaveform,
        handleLanguageChange,
        handleVersionChange,
        handleImportFile,
        handleSetTime,
        timelineExpanded,
        toggleTimeline,
        headerExpanded,    // Add this
        toggleHeader       // Add this
    };
    
    return (
        <Style>
            <GlobalStyle />
            <NotificationSystem ref={notificationSystem} />
            
            {loading && <Loading>{loading}</Loading>}
            
            <div className="main-container">
                {/* Conditionally render the header based on headerExpanded state */}
                {headerExpanded && <MultiColumnHeader {...props} />}
                
                {/* Header toggle button - always visible */}
                <div className="header-collapse-control">
                    <Tooltip title={headerExpanded ? "Hide Header" : "Show Header"}>
                        <IconButton 
                            onClick={toggleHeader}
                            size="small"
                            color="primary"
                        >
                            {headerExpanded ? 
                                <KeyboardArrowUp /> : 
                                <KeyboardArrowDown />
                            }
                        </IconButton>
                    </Tooltip>
                </div>
                
                <div className="content-container">
                    <div 
                        className="player-container"
                        style={{ 
                            maxHeight: contentMaxHeight
                        }}
                    >
                        <Player 
                            player={player}
                            setPlayer={setPlayer}
                            currentTime={currentTime}
                            setCurrentTime={setCurrentTime}
                            playing={playing}
                            setPlaying={setPlaying}
                        />
                    </div>
                    
                    <div 
                        className="subtitles-container"
                        style={{ 
                            maxHeight: contentMaxHeight
                        }}
                    >
                        <MultiColumnSubtitles 
                            {...props}
                            currentTime={currentTime}
                            setCurrentTime={setCurrentTime}
                        />
                    </div>
                </div>
                
                {/* Timeline container with collapse control */}
                <div 
                    className="timeline-container"
                    style={{ 
                        height: `${timelineHeight}px`,
                    }}
                >
                    {/* Collapse control - always visible */}
                    <div 
                        className={`timeline-collapse-control ${!timelineExpanded ? 'collapsed' : ''}`}
                        onClick={toggleTimeline}
                        sx={{
                            minHeight: '24px',
                        }}
                    >
                        {timelineExpanded ? (
                            <>
                                <span>Hide Timeline</span>
                                <KeyboardArrowDownIcon className="collapse-icon" fontSize="small" />
                            </>
                        ) : (
                            <>
                                <span>Show Timeline</span>
                                <KeyboardArrowUpIcon className="collapse-icon" fontSize="small" />
                            </>
                        )}
                    </div>
                    
                    {/* Only render timeline content if expanded */}
                    {timelineExpanded && (
                        <MultiColumnTimeline
                            columns={columns}
                            currentTime={currentTime}
                            currentIndex={currentIndex}
                            player={player}
                            handleSetTime={handleSetTime}
                        />
                    )}
                </div>
            </div>
        </Style>
    );
}